import { Filter } from '@cubejs-client/core';

import {
  HCP_IN_HCO_CUBE_NAME,
  HCO_HIERARCHY_CUBE_NAME,
  HCP_HCO_CUBE_NAME,
  HCO_ALL_LOCATIONS_CUBE_NAME,
  INDIVIDUAL_PRODUCT_ATTENDEE_CUBE_NAME,
} from './constants';

export function buildCurrentLevelFilters(hcoBk: string): Filter[] {
  return [
    {
      member: `${HCP_IN_HCO_CUBE_NAME}.child_hco_bk`,
      operator: 'equals',
      values: [hcoBk],
    },
  ];
}

export function buildAllDepartmentsFilters(hcoBk: string): Filter[] {
  return [
    {
      or: [
        {
          member: `${HCO_HIERARCHY_CUBE_NAME}.hco_bk`,
          operator: 'equals',
          values: [hcoBk],
        },
        {
          member: `${HCO_HIERARCHY_CUBE_NAME}.path_mod`,
          operator: 'contains',
          values: [hcoBk],
        },
      ],
    },
  ];
}

export function buildHcoScopeFilters(hcoBk: string, includeAllDepartments: boolean): Filter[] {
  return includeAllDepartments ? buildAllDepartmentsFilters(hcoBk) : buildCurrentLevelFilters(hcoBk);
}

/**
 * Scope filters for the HCO secondary-address (locations) widget.
 *   - current-level: the locations cube's own `hco_bk` — this widget shows the
 *     selected node's addresses, not those of its affiliated HCPs.
 *   - all-departments: adds `hcos_all_locations.address_id set`. The query joins
 *     through `hco` to `hco_hierarchy`, so a department with no secondary address
 *     would otherwise yield a phantom all-NULL row. The guard drops it, keeping
 *     the row count and the "see all" threshold accurate.
 */
export function buildHcoLocationScopeFilters(hcoBk: string, includeAllDepartments: boolean): Filter[] {
  if (includeAllDepartments) {
    return [
      ...buildAllDepartmentsFilters(hcoBk),
      { member: `${HCO_ALL_LOCATIONS_CUBE_NAME}.address_id`, operator: 'set' },
      { member: `${HCP_HCO_CUBE_NAME}.hcp_hco_pk`, operator: 'set' },
    ];
  }

  return [{ member: `${HCO_ALL_LOCATIONS_CUBE_NAME}.hco_bk`, operator: 'equals', values: [hcoBk] }];
}

/**
 * Scope filters for queries that enumerate the individual HCPs in an HCO's
 * subtree (e.g. the structure HCP table/PPOC options, or resolving hcp_bks to
 * feed a downstream interaction query).
 *
 * This differs from {@link buildHcoScopeFilters}, which scopes aggregate
 * count/coverage queries rooted at the current node:
 *   - current-level: also requires `hco_path contains hcoBk` so descendants of
 *     the selected node are included, not just its direct children.
 *   - all-departments: adds `hcp_in_hco.hcp_bk set`. The query is rooted at
 *     hco_hierarchy with a LEFT JOIN to hcp_in_hco, so a department with no
 *     affiliated HCPs would otherwise yield a phantom all-NULL row. Excluding
 *     those keeps the row total aligned with countDistinct(hcp_bk).
 */
export function buildHcpScopeFilters(hcoBk: string, includeAllDepartments: boolean): Filter[] {
  if (includeAllDepartments) {
    return [
      {
        or: [
          { member: `${HCO_HIERARCHY_CUBE_NAME}.hco_bk`, operator: 'equals', values: [hcoBk] },
          { member: `${HCO_HIERARCHY_CUBE_NAME}.path_mod`, operator: 'contains', values: [hcoBk] },
        ],
      },
      { member: `${HCP_IN_HCO_CUBE_NAME}.hcp_bk`, operator: 'set' },
    ];
  }

  return [
    { member: `${HCP_IN_HCO_CUBE_NAME}.child_hco_bk`, operator: 'equals', values: [hcoBk] },
    { member: `${HCP_IN_HCO_CUBE_NAME}.hco_path`, operator: 'contains', values: [hcoBk] },
  ];
}

export function buildHcoHcpBridgeScopeFilters(hcoBk: string, includeAllDepartments: boolean): Filter[] {
  if (includeAllDepartments) {
    return [...buildAllDepartmentsFilters(hcoBk), { member: `${HCP_HCO_CUBE_NAME}.hcp_bk`, operator: 'set' }];
  }

  return [{ member: `${HCP_HCO_CUBE_NAME}.hco_bk`, operator: 'equals', values: [hcoBk] }];
}

export function buildResolverScopeFilters(hcoBk: string, includeAllDepartments: boolean): Filter[] {
  if (includeAllDepartments) {
    return [
      { member: `${HCP_IN_HCO_CUBE_NAME}.hco_path`, operator: 'contains', values: [hcoBk] },
      { member: `${HCP_IN_HCO_CUBE_NAME}.hcp_bk`, operator: 'set' },
    ];
  }

  return [
    { member: `${HCP_IN_HCO_CUBE_NAME}.child_hco_bk`, operator: 'equals', values: [hcoBk] },
    { member: `${HCP_IN_HCO_CUBE_NAME}.hco_path`, operator: 'contains', values: [hcoBk] },
    { member: `${HCP_IN_HCO_CUBE_NAME}.hcp_bk`, operator: 'set' },
  ];
}

export function buildHcoScopeEngagementFilters(hcoBk: string, includeAllDepartments: boolean): Filter[] {
  return [
    ...buildHcoHcpBridgeScopeFilters(hcoBk, includeAllDepartments),
    { member: `${INDIVIDUAL_PRODUCT_ATTENDEE_CUBE_NAME}.is_future_interaction`, operator: 'equals', values: ['false'] },
  ];
}
